/**
 * アイコン寸法定数 — 用途別の一元管理。
 * 使い方: `boxSize={iconSize.labeledButton}` のように参照する。
 *
 * 本ファイルが用途別 icon 寸法の SoT。 値を追加する時は、用途 (どの役割の icon か)
 * と design / DS 側の出典を各 token の docstring に必ず書く (同値でも役割が違えば
 * token を分ける)。
 *
 * 値は px 文字列で持つ (chakra の boxSize は数値を spacing token として
 * 解決するため、数値リテラルは px にならない)。
 */
export const iconSize = {
  /** ラベル付きボタンの leading / trailing icon (20px) */
  labeledButton: '20px',
  /** .btn-guide 相当のガイド導線ボタン (14px) */
  guideButton: '14px',
  /**
   * メニュー項目の先頭 icon (20px)。 design 正: `css/components.css` の `.ecl-menu-item > svg`
   * = 20px 固定 (UI 辞書 dictionary/components.md の `.ecl-menu-item` 行)。 [DS差異] DS の
   * IconWrapper は 24px。 呼び出し側の boxSize に依らず `RichActionMenu` が揃える。
   * labeledButton (20px) と同値だが「ボタンの icon」 ではなく「メニュー項目の目印」 なので分ける。
   */
  menuItem: '20px',
  /** テーブル・グリッド内のインライン icon (16px) */
  gridInline: '16px',
  /**
   * Alert 右端の閉じる (×) ボタンの icon (16px)。
   * design 正: `design/js/nav.js` の `injectDismiss` が作る `.ecl-alert-dismiss` の
   * svg が `width="16" height="16"` (ボタンの箱自体は 28px 角)。
   * gridInline (16px) と同値だが「表・グリッド内のインライン icon」 とは別役割なので分ける。
   */
  alertDismiss: '16px',
  /**
   * Compact Alert の status icon (24px)。UI 辞書 alert-placement.md §0「アイコン24px」。
   * React mock (mock-app/react-app) と同じ名前。
   */
  alertStatus: '24px',
  /**
   * 一覧の操作列 (行末の編集 / 削除など icon-only ボタン) の icon (20px)。
   * design 正: `design/css/components.css` の
   * `.ecl-table td.cell-actions .btn.btn-square svg { width: 20px }`。
   * DS 既定 (24px) だと 32px ボタンに対して詰まり、gridInline (16px) だと
   * FA glyph 内の余白のぶん小さく見えるため、design 実測値を別役割として持つ。
   */
  rowAction: '20px',
  /**
   * 操作列のうち **編集アイコン** だけ 22px。
   * design 正: `.ecl-table td.cell-actions .btn.btn-square[title*="編集"] svg { width: 22px }`。
   * pen-to-square は細い四角枠で glyph が軽く小さく見えるため、他アクション (20px) と
   * 光学的に同じ大きさに見せる意図的な差 (= optical sizing)。
   */
  rowActionEdit: '22px',
  /**
   * 48 点データを開く導線の `FaTableRegular` (24px)。
   * design 正: 2026-10-20 デザイナー指定「48 点データ画面を開く導線のアイコンは DS の
   * `<FaTableRegular boxSize="24px" />`」 (8080 `css/components.css` の `.s48-link` も 24px)。
   * 操作列に置いても rowAction (20px) ではなくこちらを使う (役割が違うので token を分ける)。
   * React mock (mock-app/react-app) と同じ名前。
   */
  supply48Link: '24px',
  /**
   * カード型の入口 (システム管理トップ) の左に置く icon (22px)。 design 正: `admin/index.html` の
   * `icon()` が出す svg = `width="22" height="22"` (40px 角の淡い accent 地の箱に入れる)。
   * rowActionEdit (22px) と同値だが「行の編集ボタン」 ではなく「入口カードの目印」 という別役割なので分ける。
   */
  hubCardIcon: '22px',
  /**
   * 項目ラベル横の補足 (?) icon (18px)。 tooltip の trigger として置くもの。
   * DS 正: `@enechain/ecloud-designsystem` の `src/helper/index.tsx` の `FormHelper`
   * が `<FaCircleQuestionRegular boxSize="18px" color="Object.midEmphasis" />` を
   * 規定している (DS icon の素の既定 24px ではなくこちらが正)。
   * design 正: `design/css/components.css` の `.head-info > svg { width: 18px }`。
   */
  labelHint: '18px',
  /**
   * 入力欄の中に重ねる装飾 icon (18px)。日付欄の左に置くカレンダー等。
   * design 正: `design/css/components.css` の
   * `.date-input-wrap .date-icon { width: 18px; height: 18px }`。
   * labelHint (18px) と同値だが「ラベル脇の補足」ではなく「入力欄の内側」という
   * 別役割なので token を分ける (寸法表の他 alias と同じ構図)。
   */
  inputAdornment: '18px',
  /**
   * バッジ内の leading icon (14px)。design 正: `design/admin/loss-rate.html` の
   * `.lr-revision-badge svg { width: 14px }`。高さ 24px の `.badge` に対して
   * gridInline (16px) だと詰まる。
   */
  badgeInline: '14px',
  /**
   * 入力欄直下の注意・補足メッセージの先頭 icon (14px)。
   * design 正: `design/css/components.css` の
   * `.form-warn-msg svg { width: 14px; height: 14px; }`。
   * gridInline (16px) だと sm (12px) の本文に対して大きく、
   * guideButton (14px) は同値だが「ガイド導線ボタン」という別役割のため分ける。
   */
  fieldMessage: '14px',
  /**
   * ソート可能な列見出しのソート標 (14px)。
   * design 正: `design/css/components.css` の
   * `table[data-sortable] th .sort-arrow svg { width: 14px; height: 14px; }`。
   */
  sortIndicator: '14px',
  /**
   * バッジ・chip の中に置く極小 icon (12px)。
   * design 正: `design/admin/approval-flow.html:1052` の要設定バッジ内 warning icon が 12。
   * fieldMessage (14px) はフィールド直下のメッセージ用で、 バッジ内はさらに 1 段小さい。
   */
  badgeIcon: '12px',
  /**
   * 高さ 18px の件数 pill (タブの印) の中で、件数の前に置く icon (11px)。
   * design 正: `design/plan/submission.html` の `.ecl-segmented .internal-count-badge svg`
   * (11px)。badgeIcon (12px) を入れると件数の数字 (11px) より大きく見える。
   */
  countPillIcon: '11px',
  /**
   * 表・グリッドの中に差し込む操作ヒント行の leading icon (12px)。
   * design 正: `design/demand/forecast-today.html` の `.koma-edit-divider-row` が
   * 埋め込む矢印 svg が `width="12" height="12"` (行の文字は `--font-size-xs`)。
   * badgeIcon (12px) と同値だが「バッジ・chip の中」ではなく「行として差し込む
   * ヒント」という別役割なので分ける (gridInline / alertDismiss を分けているのと
   * 同じ判断)。
   */
  gridEditHint: '12px',
  /**
   * 外部リンク (プロダクト外遷移) の trailing icon (12px)。
   * design 正: `design/admin/weather-forecast-points.html` の出典リンクが
   * `width="12" height="12"`、`design/ui-feasibility/screens/weather-forecast.md`
   * も「external icon 12px」。DS 既定 (24px) は sm (12px) の行に対して大きい。
   */
  externalLink: '12px',
  /**
   * accordion の開閉標 (chevron) の icon (20px)。
   * DS 正: `@enechain/ecloud-designsystem` の `AccordionIconButton` が
   * 32px の ghost ボタンに `boxSize="20px"` の chevron を載せる。
   * labeledButton (20px) と同値だが「ラベル付きボタンの leading/trailing」 とは
   * 別役割なので分ける (DS 側が寸法を変えた時に追随する先を取り違えないため)。
   */
  accordionDisclosure: '20px',
  /**
   * stepper の丸バッジ内に置く完了マーク (16px)。
   * 40px 前後の円バッジの中に載せる寸法で、 既存 3 実装
   * (`WizardStepper` / `BigStepAccordion` / `ApprovalWizard`) の実測値と同じ。
   * gridInline (16px) と同値だが「表セル内の inline icon」 とは別役割なので分ける
   * (accordionDisclosure と labeledButton を分けているのと同じ判断)。
   */
  stepperMark: '16px',
  /**
   * CSV 取込ステッパーの 40px の円に載せる完了マーク (24px)。
   * design 正: `.wizard-step.done .wizard-step-num::before` の `mask-size: 60%`
   * (40px の円の 60% = 24px。 UI 辞書 components/wizard-stepper.md)。
   * stepperMark (16px) は他 3 実装 (WizardStepper 等) の実測で、 役割が同じでも
   * 寸法の出典が別なので分ける。
   */
  stepperDoneMark: '24px',
  /**
   * サブステップ行 左端の完了マーク (45px)。
   * design 正: `design/demand/case-detail.html` の `.substep::before` が 32px の枠に
   * `mask-size: 141%` で fa-circle-check を敷き、 **円の実寸を 32px** にしている
   * (fa の円は viewBox 24 のうち 17 = 71% しか占めないため、 24/17 = 141% に拡げて
   * 余白を切り落とす)。 DS icon component は viewBox をそのまま描くので、 同じ見た目に
   * するには枠ではなく **svg 自体**を 32 / 0.71 = 45px にする。 枠 (32px) からはみ出す
   * ぶんは glyph の透明余白なので見えない。
   * stepperMark (16px) は円バッジ **の中**に載せる別役割。
   */
  subStepCompleteMark: '45px',
  /** CSV アップロードの dropzone 等、面の中の装飾 icon (36px) */
  decorative: '36px',
  /**
   * CSV アップロード画面のファイル選択枠の icon (32px)。 design 正: `css/components.css` の
   * `.ecl-upload-area-icon svg { width: 32px }`。 decorative (36px) より 1 段小さい。
   */
  uploadArea: '32px',
  /**
   * 選択済みファイル情報の行頭のファイル icon (18px)。 design 正: `css/components.css` の
   * `.up-file-info-icon svg { width: 18px }`。
   */
  fileInfo: '18px',
  /**
   * 空状態パネルの icon (80px)。DS `PageError` の `boxSize="80px"` が正で、
   * `design/ui-feasibility/components/empty-panel.md` の使用トークン表がそれを写している。
   * decorative (36px) とは別物 — あちらは dropzone の装飾で、面が丸ごと空の時の主役ではない。
   */
  emptyPanel: '80px',
} as const satisfies Record<string, string>
